/**
 * Adapter between TipTap answer node attrs and flat builder props.
 *
 * Node attrs (camelCase, split): `toolLabel`, `assessmentType`, `assessmentOptions`.
 *
 * Builder props (snake_case, flat): legacy zyBooks / symtool shape used by tool-settings,
 * mathlive, and import/export.
 *
 * Use `attrsToBuilderProps` to read, `mergeBuilderPatchIntoAttrs` to patch,
 * `applyToolLabelToAttrs` to switch tools. Normalizes HTML entities in option strings on import.
 */

import { isPlainObject } from '@/lib/primitive-utils';
import { unescapeXml } from '@/lib/utils';

import type { BuilderPropertyRecord } from '../tool-defaults';
import { DEFAULT_SHORT_ANSWER_TOOL_LABEL, shortAnswerToolsDefaults } from '../tool-defaults';

export function getShortAnswerToolDefaultPropertiesByLabel(label: string): BuilderPropertyRecord {
    const tool = shortAnswerToolsDefaults.find(t => t.label === label);
    if (!tool) {
        throw new Error(`Unknown short answer tool label: ${label}`);
    }
    return { ...tool.defaultProperties };
}

export function getShortAnswerToolLabelByProperties(properties: BuilderPropertyRecord): string {
    return shortAnswerToolsDefaults.find(({ criteria }) => criteria(properties))?.label ?? DEFAULT_SHORT_ANSWER_TOOL_LABEL;
}

export const SHORT_ANSWER_TOOL_LABELS: readonly string[] = shortAnswerToolsDefaults.map(({ label }) => label);

export type ShortAnswerAnswerAttrsSlice = {
    assessmentType?: string | null;
    assessmentOptions?: Record<string, unknown> | null;
    toolLabel?: string;
};

/** ZyXML attribute values for JSON-string fields often arrive with HTML entities (`&quot;`) still encoded. */
export function normalizeAssessmentOptionStrings(opts: Record<string, unknown>): BuilderPropertyRecord {
    const out: BuilderPropertyRecord = {};
    for (const [key, value] of Object.entries(opts)) {
        out[key] = typeof value === 'string' ? unescapeXml(value) : value;
    }
    return out;
}

export function parseAssessmentOptionsJson(raw: string | null | undefined): BuilderPropertyRecord {
    if (raw == null || raw === '') {
        return {};
    }
    try {
        const v = JSON.parse(raw) as unknown;
        return isPlainObject(v) ? normalizeAssessmentOptionStrings(v) : {};
    } catch {
        return {};
    }
}

export function attrsToBuilderProps(attrs: ShortAnswerAnswerAttrsSlice): BuilderPropertyRecord {
    const opts = normalizeAssessmentOptionStrings({ ...(attrs.assessmentOptions ?? {}) });
    const assessmentType = attrs.assessmentType ?? (opts.assessment_type as string | undefined) ?? 'string';
    const toolLabel = attrs.toolLabel;

    delete opts.assessment_type;

    const out: BuilderPropertyRecord = {
        ...opts,
        assessment_type: assessmentType,
    };
    if (toolLabel != null && toolLabel !== '') {
        out.tool_label = toolLabel;
    }
    return out;
}

export type SplitBuilderFlatPm = {
    assessmentType: string;
    toolLabel: string;
    assessmentOptions: Record<string, unknown>;
};

export function splitBuilderToFlatAndOptions(merged: BuilderPropertyRecord): SplitBuilderFlatPm {
    const assessmentType = (merged.assessment_type as string) ?? 'string';
    const toolLabel = (merged.tool_label as string) ?? DEFAULT_SHORT_ANSWER_TOOL_LABEL;

    const options: BuilderPropertyRecord = { ...merged };
    delete options.assessment_type;
    delete options.tool_label;

    return {
        assessmentType,
        toolLabel,
        assessmentOptions: options as Record<string, unknown>,
    };
}

export function toolDefaultsToAnswerAttrs(
    defaults: BuilderPropertyRecord,
    preserve: {
        answers?: string[];
        preferredIndex?: number | null;
    },
): Record<string, unknown> {
    return {
        answers: preserve.answers,
        preferredIndex: preserve.preferredIndex,
        ...splitBuilderToFlatAndOptions(defaults),
    };
}

export function mergeBuilderPatchIntoAttrs(attrs: Record<string, unknown>, patch: BuilderPropertyRecord): Record<string, unknown> {
    const base = attrsToBuilderProps(attrs);
    const merged: BuilderPropertyRecord = { ...base, ...patch };
    const { toolLabel, assessmentType, assessmentOptions } = splitBuilderToFlatAndOptions(merged);
    return {
        id: attrs.id ?? null,
        answers: attrs.answers,
        preferredIndex: attrs.preferredIndex ?? null,
        toolLabel,
        assessmentType,
        assessmentOptions,
    };
}

export function allKeysUsedByAnyTool(): Set<string> {
    const keys = new Set<string>();
    for (const tool of shortAnswerToolsDefaults) {
        for (const k of Object.keys(tool.defaultProperties)) {
            keys.add(k);
        }
    }
    return keys;
}

export function applyToolLabelToAttrs(label: string, currentAttrs: Record<string, unknown>): Record<string, unknown> {
    const defaults = getShortAnswerToolDefaultPropertiesByLabel(label);
    const fromTool = toolDefaultsToAnswerAttrs(defaults, {
        answers: currentAttrs.answers as string[] | undefined,
        preferredIndex: currentAttrs.preferredIndex as number | null | undefined,
    });
    return {
        id: currentAttrs.id ?? null,
        ...fromTool,
        answers: (currentAttrs.answers as string[] | undefined) ?? (fromTool.answers as string[]),
        preferredIndex: currentAttrs.preferredIndex ?? null,
        toolLabel: label,
    };
}
